import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

export const MANIFEST_START_TIME_DISPLAY_FORMAT = "MMM D, YYYY h:mm A";

export function formatManifestStartTime(value) {
  if (!value) return "None";
  const parsed = dayjs(value);
  return parsed.isValid()
    ? parsed.format(MANIFEST_START_TIME_DISPLAY_FORMAT)
    : String(value);
}

export function toManifestStartTimeIso(value) {
  if (value == null || value === "") return null;
  const parsed = dayjs.isDayjs(value) ? value : dayjs(value);
  return parsed.isValid() ? parsed.toISOString() : null;
}

export default function ManifestStartTimePicker({
  value,
  onChange,
  label = "Start Date & Time",
  disabled = false,
  fullWidth = true,
  size = "small",
  showClear = true,
  /** When true, only call onChange after the picker is closed/accepted (or Clear). */
  commitOnAccept = false,
  sx = {},
}) {
  const [draft, setDraft] = useState(() =>
    value ? dayjs(value) : null,
  );

  useEffect(() => {
    setDraft(value ? dayjs(value) : null);
  }, [value]);

  const pickerValue = draft?.isValid?.() ? draft : null;

  const commit = (nextDayjs) => {
    onChange(toManifestStartTimeIso(nextDayjs));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, ...sx }}>
        <Box sx={{ flex: fullWidth ? 1 : "none", minWidth: fullWidth ? 0 : 240 }}>
          {label ? (
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, mb: 0.75, color: "text.secondary" }}
            >
              {label}
            </Typography>
          ) : null}
          <DateTimePicker
            value={pickerValue}
            onChange={(next) => {
              setDraft(next);
              if (!commitOnAccept) {
                commit(next);
              }
            }}
            onAccept={(next) => {
              if (commitOnAccept) {
                setDraft(next);
                commit(next);
              }
            }}
            disabled={disabled}
            format={MANIFEST_START_TIME_DISPLAY_FORMAT}
            ampm
            slotProps={{
              textField: {
                size,
                fullWidth,
                placeholder: "Optional",
              },
              field: {
                clearable: false,
              },
              actionBar: commitOnAccept
                ? { actions: ["clear", "accept"] }
                : undefined,
            }}
          />
        </Box>
        {showClear && (
          <Button
            size="small"
            disabled={disabled || !pickerValue}
            onClick={() => {
              setDraft(null);
              onChange(null);
            }}
            sx={{ mb: 0.25, textTransform: "none", flexShrink: 0 }}
          >
            Clear
          </Button>
        )}
      </Box>
    </LocalizationProvider>
  );
}
